import { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { useToast } from '../ToastProvider';
import { inputClass, sanitizeSlotList } from '../FormKit';
import { toDateKey, weekdayKeyOf, formatDayLabel } from './dateUtils';
import { computeEffectiveSlots, bookingCountsForDate, withRemainingCapacity } from './useEffectiveSlots';
import { formatTime12h } from '../../utils/helpers';

function blankSlot() {
  return {
    _key: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    startTime: '06:00',
    endTime: '07:00',
    capacity: 1,
    isActive: true,
  };
}

export default function DateEditorPanel({
  date, weeklySlots, slotOverrides, bookings = [], onSaveOverride, onResetOverride, onClose, variant = 'overlay', readOnly = false,
}) {
  const { showToast } = useToast();
  const dateKey = toDateKey(date);
  const weekdayKey = weekdayKeyOf(date);
  const rawEffective = computeEffectiveSlots(weeklySlots, slotOverrides, dateKey, weekdayKey);
  const bookingCounts = bookingCountsForDate(bookings, dateKey);
  const effective = { ...rawEffective, slots: withRemainingCapacity(rawEffective.slots, bookingCounts) };
  const [draftSlots, setDraftSlots] = useState(() => effective.slots.map(s => ({ ...s })));
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  function updateSlot(index, field, value) {
    setDraftSlots(prev => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  }

  function deleteSlot(index) {
    setDraftSlots(prev => prev.filter((_, i) => i !== index));
  }

  function addSlot() {
    setDraftSlots(prev => [...prev, blankSlot()]);
  }

  function markClosed() {
    setDraftSlots([]);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await onSaveOverride(dateKey, sanitizeSlotList(draftSlots));
      showToast('Date updated');
      onClose?.();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    setResetting(true);
    try {
      await onResetOverride(dateKey);
      showToast('Reverted to default');
      // The overlay variant unmounts via onClose, which re-seeds the draft
      // on next open. The inline variant (DayView) never unmounts, so the
      // draft must be reseeded explicitly here or it'd keep showing the
      // just-removed override's slots.
      setDraftSlots((weeklySlots[weekdayKey] || []).map(s => ({ ...s })));
      onClose?.();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to reset', 'error');
    } finally {
      setResetting(false);
    }
  }

  const badge = effective.isClosed
    ? { label: 'Closed', className: 'bg-[#fce8e6] dark:bg-red-950/40 text-[#d93025] dark:text-red-300' }
    : effective.isOverridden
      ? { label: 'Custom hours', className: 'bg-[#f3e8fd] dark:bg-purple-950/40 text-[#9334e6] dark:text-purple-300' }
      : { label: 'Default', className: 'bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6]' };

  const content = (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#202124] dark:text-[#e8eaed]">{formatDayLabel(date)}</p>
          <span className={`inline-block mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${badge.className}`}>
            {badge.label}
          </span>
        </div>
        {variant === 'overlay' && (
          <button type="button" onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4] dark:text-[#9aa0a6] dark:hover:bg-[#202124] cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {!readOnly && (
        <div className="space-y-2">
          {draftSlots.length === 0 && (
            <p className="text-sm text-[#9aa0a6] text-center py-3">No slots for this date. Add one, or it stays closed.</p>
          )}
          {draftSlots.map((slot, index) => (
            <div key={slot._id || slot._key || index}
              className={`space-y-2 rounded-xl border p-2 border-[#e8eaed] dark:border-[#3c4043] ${slot.isActive ? 'bg-white dark:bg-[#303134]' : 'bg-[#f8fafd] dark:bg-[#202124]/50 opacity-65'}`}>
              <div className="flex flex-wrap items-center gap-2">
                <input type="time" value={slot.startTime} onChange={e => updateSlot(index, 'startTime', e.target.value)}
                  className={inputClass + ' text-xs px-2 py-1.5 w-[6.5rem] shrink-0'} />
                <input type="time" value={slot.endTime} onChange={e => updateSlot(index, 'endTime', e.target.value)}
                  className={inputClass + ' text-xs px-2 py-1.5 w-[6.5rem] shrink-0'} />
                <input type="number" min="1" value={slot.capacity}
                  onChange={e => updateSlot(index, 'capacity', Number(e.target.value) || 1)}
                  className={inputClass + ' text-xs px-2 py-1.5 w-14 shrink-0'} title="Capacity" />
              </div>
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-[#5f6368] dark:text-[#9aa0a6] cursor-pointer whitespace-nowrap" title="Toggle off to hold this slot for maintenance without deleting it">
                  Active
                  <button type="button" role="switch" aria-checked={slot.isActive}
                    onClick={() => updateSlot(index, 'isActive', !slot.isActive)}
                    className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors duration-200 cursor-pointer ${slot.isActive ? 'bg-[#1a73e8]' : 'bg-[#dadce0] dark:bg-[#3c4043]'}`}>
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ${slot.isActive ? 'translate-x-[14px]' : 'translate-x-0.5'}`} />
                  </button>
                </label>
                <button type="button" onClick={() => deleteSlot(index)}
                  className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#EA4335] text-white hover:bg-[#d93025] shrink-0 cursor-pointer">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <button type="button" onClick={addSlot}
              className="flex-1 py-2 rounded-xl border border-dashed border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] text-xs font-semibold hover:bg-[#f1f3f4] dark:hover:bg-[#202124] cursor-pointer">
              + Add Slot
            </button>
            <button type="button" onClick={markClosed}
              className="py-2 px-3 rounded-xl border border-dashed border-red-200 dark:border-red-900 text-[#EA4335] text-xs font-semibold hover:bg-red-50 dark:hover:bg-red-950/30 cursor-pointer">
              Mark Closed
            </button>
          </div>
        </div>
      )}

      {readOnly && (
        <div className="space-y-2">
          {effective.slots.length === 0 && <p className="text-sm text-[#9aa0a6] text-center py-3">No slots for this date.</p>}
          {effective.slots.map((slot, index) => (
            <div key={slot._id || index} className="rounded-xl border border-[#e8eaed] dark:border-[#3c4043] p-2 text-xs text-[#5f6368] dark:text-[#9aa0a6]">
              {formatTime12h(slot.startTime)}–{formatTime12h(slot.endTime)} · {slot.remainingCapacity}/{slot.capacity} open {!slot.isActive && '· inactive'}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#e8eaed] dark:border-[#3c4043]">
          <button type="button" onClick={handleReset} disabled={!effective.isOverridden || resetting}
            className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] hover:text-[#202124] dark:hover:text-[#e8eaed] disabled:opacity-40 cursor-pointer">
            {resetting ? 'Resetting...' : 'Reset to default'}
          </button>
          <div className="flex gap-2">
            {variant === 'overlay' && (
              <button type="button" onClick={onClose}
                className="text-xs font-semibold px-3.5 py-2 rounded-full border border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#202124] cursor-pointer">
                Cancel
              </button>
            )}
            <button type="button" onClick={handleSave} disabled={saving}
              className="text-xs font-semibold px-4 py-2 rounded-full text-white disabled:opacity-60 bg-[#1a73e8] hover:bg-[#1557b0] cursor-pointer">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (variant === 'inline') {
    return <div className="google-card bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] p-5">{content}</div>;
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={onClose} />
      <div className="relative w-full max-w-sm h-full bg-white dark:bg-[#303134] shadow-xl p-5 overflow-y-auto">
        {content}
      </div>
    </div>
  );
}
